import { Loader, Stack, useMantineTheme } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import Trans from 'next-translate/Trans';
import useTranslation from 'next-translate/useTranslation';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { reportEvent } from '~/domains/analytics';
import { SignUpHeader } from '~/domains/auth/components/SignUpHeader/SignUpHeader';
import { ErrorAlert } from '~/domains/common/components/Alerts/Alerts';
import { Banner, BannerType } from '~/domains/common/components/Banner/Banner';
import { Link } from '~/domains/common/components/Link/Link';
import { formatDollars } from '~/domains/common/utils/formatters';
import { Button } from '~/domains/design-system/Button';
import { Text } from '~/domains/design-system/Text';
import { getApiErrorCode, getErrorTranslations } from '~/domains/payments/utils/utils';
import {
  WHOP_CRYPTO_PROVISIONING_FAILED,
  type CryptoDepositArrival,
} from '~/services/wallet/interfaces/crypto-direct.interface';
import dt from '~/testing';

import { useCryptoDepositAddresses } from '../../hooks/useCryptoDepositAddresses';
import { useCryptoDepositArrivals } from '../../hooks/useCryptoDepositArrivals';
import { BackButton } from '../BackButton';

import { CryptoAddressSheet } from './CryptoAddressSheet';
import { CryptoArrivalsList } from './CryptoArrivalsList';
import { CryptoAssetList } from './CryptoAssetList';
import { CryptoNetworkList } from './CryptoNetworkList';
import { CRYPTO_DIRECT_DEPOSIT_METHOD } from './constants';

const SUPPORT_MAILTO =
  'mailto:support@splashsports.com?subject=Splash Support - Crypto Deposit&body=Hello, Splash Support';

type CryptoDirectDepositProps = {
  onBack: () => void;
};

type Selection = { network?: string; asset?: string };

/**
 * Crypto Deposit v3 — gated network → asset → confirm flow (EDGE-32059). The user picks a Whop
 * network then an asset from the server-owned catalog; an address is only created (and shown)
 * once they confirm inside `CryptoAddressSheet`. `selection` drives the three screens:
 *   - no network picked          → CryptoNetworkList
 *   - network picked, no asset  → CryptoAssetList
 *   - asset picked               → CryptoAddressSheet (owns create/reveal/retry/cooldown; it is
 *     UNMOUNTED, not just hidden, once the asset is cleared, so a revealed address leaves the DOM
 *     on close even though the query cache keeps it).
 * `CryptoArrivalsList` stays mounted underneath every screen so its cursor/poll state survives
 * screen swaps.
 */
export function CryptoDirectDeposit({ onBack }: CryptoDirectDepositProps) {
  const { t } = useTranslation('payments');
  const theme = useMantineTheme();

  const { data, isLoading, isError, error, refetch } = useCryptoDepositAddresses();
  const [selection, setSelection] = useState<Selection>({});

  const accountStatus = data?.accountStatus;
  const catalog = useMemo(() => data?.catalog ?? [], [data?.catalog]);
  const isActive = accountStatus === 'active';

  const { network: selectedNetworkName, asset: selectedAssetName } = selection;

  const selectNetwork = useCallback((network: string) => setSelection({ network }), []);
  const selectAsset = useCallback(
    (asset: string) => setSelection((current) => ({ ...current, asset })),
    []
  );
  // Close the sheet: back to the asset list for the SAME network (not all the way to the network
  // list) — the sheet owns its own screen, closing it never discards the network pick.
  const closeSheet = useCallback(
    () => setSelection((current) => ({ network: current.network })),
    []
  );
  const backToNetworkList = useCallback(() => setSelection({}), []);

  const selectedCatalogNetwork = useMemo(
    () => catalog.find((network) => network.name === selectedNetworkName),
    [catalog, selectedNetworkName]
  );

  // Return focus to the asset row that opened the sheet once the sheet closes. The sheet REAL-
  // unmounts on close (so the revealed address leaves the DOM), which kills Mantine's own
  // useFocusReturn (it needs an opened true→false transition it never sees) — so we restore focus
  // imperatively. Fired from this effect (post-unmount) rather than onClose (pre-unmount) so the
  // Modal's focus trap has released first and won't steal focus back. We match the row by its
  // accessible name (identity), NOT a stashed DOM node: Screen 2 stays mounted across the close, so
  // the row still exists; a detached-node refocus would silently no-op to <body> — the exact bug.
  const bodyRef = useRef<HTMLDivElement>(null);
  const prevAssetRef = useRef<string | undefined>(selectedAssetName);
  useEffect(() => {
    const prevAsset = prevAssetRef.current;
    prevAssetRef.current = selectedAssetName;
    if (prevAsset && !selectedAssetName && selectedNetworkName) {
      const rows = bodyRef.current?.querySelectorAll<HTMLElement>(
        `[data-test-id="${dt.payments.deposits.cryptoDirect.assetRow}"]`
      );
      const target = Array.from(rows ?? []).find(
        (row) => row.getAttribute('aria-label') === prevAsset
      );
      target?.focus();
    }
  }, [selectedAssetName, selectedNetworkName]);

  // A selected network that has dropped out of a loaded (non-empty) catalog would render a blank body
  // (network list needs !selectedNetworkName; asset list needs selectedCatalogNetwork). Reset to {} so
  // the user lands on the network list with the fresh catalog rather than a dangling selection.
  // Guarded on catalog.length > 0 so the empty-catalog state (its own screen) is unaffected. Also
  // guarded on !selectedAssetName: once an asset is picked the sheet is OPEN, and the reveal POST's
  // onSuccess writes a scoped catalog (its own catalog: [] or a degraded shape) into the SAME cache
  // entry the container reads — without this guard that write would drop the picked network from the
  // catalog and unmount the open sheet out from under the user mid-flow. The blank-body precondition
  // only ever exists with NO asset picked, so scoping the reset to that case is exact.
  useEffect(() => {
    if (selectedNetworkName && !selectedAssetName && catalog.length > 0 && !selectedCatalogNetwork) {
      setSelection({});
    }
  }, [selectedNetworkName, selectedAssetName, catalog, selectedCatalogNetwork]);

  const handleCompleted = useCallback(
    (arrival: CryptoDepositArrival) => {
      const amount = formatDollars(arrival.observedAmount, true); // already dollars
      showNotification({
        title: t('deposits.cryptoDirect.arrivals.completedNotification.title'),
        message: t('deposits.cryptoDirect.arrivals.completedNotification.message', { amount }),
        color: 'green',
        autoClose: 5000,
      });
      // Funnel parity with the legacy 'Transactions > Deposit' event. No address, no tx hash.
      reportEvent('Transactions > Deposit', {
        deposit_id: arrival.id,
        deposit_method: CRYPTO_DIRECT_DEPOSIT_METHOD,
        deposit_amount: arrival.observedAmount,
        is_custom_deposit: true,
        currency: arrival.currency,
      });
    },
    [t]
  );

  const {
    arrivals,
    status: arrivalsStatus,
    hasLoaded: hasArrivalsLoaded,
  } = useCryptoDepositArrivals({
    enabled: isActive,
    onCompleted: handleCompleted,
  });

  // Shared by the isError branch and the fail-closed branch below: same ErrorAlert + Retry shape.
  // A re-fetch CAN resolve either case — a fetch failure may be transient, and an unrecognized
  // accountStatus may turn into a known one on the next fetch.
  const renderGenericErrorCard = (title: string, message: string) => (
    <Stack spacing={theme.other.spacing._16}>
      <ErrorAlert title={title}>{message}</ErrorAlert>
      <Button
        variant="secondary-stroke"
        size="medium"
        onClick={() => refetch()}
        data-test-id={dt.payments.deposits.cryptoDirect.retryButton}
      >
        {t('deposits.cryptoDirect.error.retry')}
      </Button>
    </Stack>
  );

  const renderBody = () => {
    if (isLoading) {
      return (
        <Stack align="center" py={theme.other.spacing._32}>
          <Loader />
        </Stack>
      );
    }

    if (isError) {
      // Derive per-code copy from the plan error (KYC/geo/limit → its own message); getApiErrorCode
      // falls back to 'UNKNOWN' for a codeless failure.
      const { title, message } = getErrorTranslations(t, getApiErrorCode(error));
      return renderGenericErrorCard(title, message);
    }

    if (accountStatus === 'suspended') {
      return (
        <Stack spacing={theme.other.spacing._12}>
          <Banner
            type={BannerType.ERROR}
            minimal={false}
            allowClose={false}
            message={t('deposits.cryptoDirect.suspended.title')}
          />
          <Text variant="body-large">
            <Trans
              i18nKey="payments:deposits.cryptoDirect.suspended.message"
              components={{
                supportLink: (
                  <Link
                    href={SUPPORT_MAILTO}
                    target="_blank"
                    style={{ color: 'inherit', textDecoration: 'underline' }}
                  />
                ),
              }}
            />
          </Text>
        </Stack>
      );
    }

    // EDGE-32059 (Milan finding 1): 'provisioning_failed' is wire-admitted (CRYPTO_ACCOUNT_STATUSES)
    // but has no network/asset/sheet screen. v3 never actually emits it — a provisioning cap surfaces
    // as a 429 cooldown on the create POST, not as a GET status — yet the schema still admits it, so
    // render a fail-SAFE terminal card (support CTA, NO Retry, NO spinner) in case the wire ever
    // carries it. Placed ABOVE the fail-closed catch-all so it gets this dedicated card instead of the
    // fail-GENERIC error whose Retry would re-fetch the same terminal status forever.
    if (accountStatus === WHOP_CRYPTO_PROVISIONING_FAILED) {
      return (
        <Stack spacing={theme.other.spacing._16}>
          <ErrorAlert title={t('deposits.cryptoDirect.provisioningFailed.title')}>
            {t('deposits.cryptoDirect.provisioningFailed.message')}
          </ErrorAlert>
          <Link href={SUPPORT_MAILTO}>
            {t('deposits.cryptoDirect.provisioningFailed.supportCta')}
          </Link>
        </Stack>
      );
    }

    // EDGE-32059: fail-closed for any accountStatus the FE doesn't have a screen for — the schema's
    // 'unknown' sentinel, or (belt-and-suspenders) any future wire literal not handled above.
    // 'provisioning_failed' has its own terminal card above; only 'unknown'/unrecognized literals
    // reach here. 'none' (the new unprovisioned steady state) is admitted alongside 'active' and
    // 'provisioning': all three render the same network/asset/sheet flow below, driven by the
    // server-owned catalog. Merge-order safety: a backend that ships a new status before this FE
    // lands must never show a false network list.
    if (
      accountStatus !== 'active' &&
      accountStatus !== 'provisioning' &&
      accountStatus !== 'none'
    ) {
      const { title, message } = getErrorTranslations(t, 'UNKNOWN');
      return renderGenericErrorCard(title, message);
    }

    return (
      <>
        {/* EDGE-32114 (Mark's ruling — one gutter back control, no inline trail): the old
            `‹ Crypto deposit › …` breadcrumb is gone. The page-level gutter BackButton (below,
            → deposit methods) renders on every step; the asset list adds its own gutter back
            (→ network list). The sheet steps keep the sheet's own "‹ Back" (CryptoAddressSheet). */}
        {!selectedNetworkName &&
          (catalog.length === 0 ? (
            // EDGE-32059 (Milan finding 3): an empty catalog on the network-list path (a genuinely
            // empty GET, or one whose entries the schema all dropped) would otherwise render a blank
            // <ul> with nothing to click. Show a recoverable empty state instead — Retry refetches.
            // Only on the network-list screen; a picked network is the asset list / sheet path.
            <Stack spacing={theme.other.spacing._16}>
              <Text variant="body-large">{t('deposits.cryptoDirect.catalogEmpty')}</Text>
              <Button
                variant="secondary-stroke"
                size="medium"
                onClick={() => refetch()}
                data-test-id={dt.payments.deposits.cryptoDirect.retryButton}
              >
                {t('deposits.cryptoDirect.error.retry')}
              </Button>
            </Stack>
          ) : (
            <CryptoNetworkList networks={catalog} onSelect={selectNetwork} />
          ))}

        {/* Screen 2 stays MOUNTED beneath the open sheet (no `&& !selectedAssetName`): the sheet
            renders ABOVE a still-mounted asset list so, on close, focus can return imperatively to
            the asset row that opened it (see the close-transition effect above). The revealed
            address lives inside the sheet and still leaves the DOM when the sheet unmounts. */}
        {selectedNetworkName && selectedCatalogNetwork && (
          <CryptoAssetList
            network={selectedCatalogNetwork}
            onSelect={selectAsset}
            onBack={backToNetworkList}
          />
        )}

        {selectedNetworkName && selectedAssetName && (
          <CryptoAddressSheet
            opened={!!selectedAssetName}
            onClose={closeSheet}
            onBackToNetworkList={backToNetworkList}
            network={selectedNetworkName}
            asset={selectedAssetName}
          />
        )}
      </>
    );
  };

  return (
    <Stack
      spacing={theme.other.spacing._32}
      data-test-id={dt.payments.deposits.cryptoDirect.container}
    >
      <div>
        {/* EDGE-32114/EDGE-32128 (folded): the page-level gutter back exits the crypto flow to the
            deposit methods and renders on every step (it is the network-list step's only back).
            Deleting CryptoBreadcrumb left this icon-only button unlabelled — a WCAG 4.1.2 gap — so
            give it the approved `backToMethods` name. */}
        <BackButton
          onClick={onBack}
          aria-label={t('deposits.cryptoDirect.breadcrumb.backToMethods')}
        />
        <SignUpHeader
          title={t('deposits.cryptoDirect.title')}
          copy={t('deposits.cryptoDirect.copy')}
          isLogoVisible={false}
        />
      </div>
      {/* Polite live region announces async state transitions (loading→ready, error, suspended).
          NOT aria-atomic: a new arrival must announce only its own row, not re-read the whole
          screen. See EDGE-31584 to strengthen this region's test. */}
      <div ref={bodyRef} aria-live="polite">
        {renderBody()}
      </div>

      {/* Mounted underneath every screen (not gated by screen or accountStatus) so its hook keeps
          cursor/list state in the component itself — unmounting it across a network→asset→sheet swap
          would lose that state. Only its `enabled` flag (accountStatus === 'active') gates the actual
          poll. EDGE-32112: the SECTION is gated on the arrivals hook having REACHED ready at least once
          (`hasArrivalsLoaded`), not on the container's `!isLoading`. This subsumes the initial-load
          case (the arrivals fetch hasn't resolved yet during the addresses-GET load → not loaded) AND
          covers a non-active account, whose fetch is disabled and so never becomes ready — both would
          otherwise show a bare "Recent transfers" heading over blank content (Duc's report). The latch
          stays true across transient post-ready poll errors, so a poll blip never tears the section
          (with its own self-healing null-content-on-error) back down. State preservation is unaffected:
          the hook stays mounted regardless of this render gate. */}
      {hasArrivalsLoaded && <CryptoArrivalsList arrivals={arrivals} status={arrivalsStatus} />}
    </Stack>
  );
}
